// ============================================================================
// Department Report — export helpers (PDF + Excel)
// ISOLATED MODULE. Self-contained. Does not import from or modify any existing
// daily-summary / work-summary code. Reuses libs already in package.json
// (jspdf, xlsx) — no new dependencies.
// ============================================================================

import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

// Strip markdown-ish noise so the PDF text stays clean and readable.
const cleanText = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/\*\*/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/`/g, '')
    .replace(/\r/g, '')
    .trim();
};

// ---------------------------------------------------------------------------
// Shared PDF rendering toolkit: a markdown-aware writer + a table drawer.
// These let the PDF show structured content (headings, bold, bullets, tables)
// instead of raw markdown / dropped visualizations.
// ---------------------------------------------------------------------------

// Remove inline visualization tags / raw JSON blocks from prose text so they
// don't leak into the rendered paragraphs (we render visualizations separately).
const stripVizTags = (str) =>
  String(str || '')
    .replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, '')
    .replace(/\{\s*"type"\s*:\s*"(line|bar|pie|table)"[\s\S]*?\}\s*/gi, '')
    .replace(/\r/g, '');

function makePdfCursor(doc, margin) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - margin * 2;
  const state = { y: margin };

  const ensureSpace = (needed) => {
    if (state.y + needed > pageHeight - margin) {
      doc.addPage();
      state.y = margin;
    }
  };

  // Render one line that may contain **bold** spans, honoring word wrap.
  const writeRichLine = (line, fontSize, baseStyle, indent = 0) => {
    const segments = String(line).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    const lineHeight = fontSize * 1.4;
    let x = margin + indent;
    ensureSpace(lineHeight);
    segments.forEach((seg) => {
      const bold = /^\*\*[^*]+\*\*$/.test(seg);
      const txt = bold ? seg.replace(/\*\*/g, '') : seg;
      doc.setFont('helvetica', bold ? 'bold' : baseStyle);
      doc.setFontSize(fontSize);
      // Wrap within remaining width.
      const words = txt.split(/(\s+)/);
      words.forEach((w) => {
        const wWidth = doc.getTextWidth(w);
        if (x + wWidth > margin + maxWidth) {
          state.y += lineHeight;
          ensureSpace(lineHeight);
          x = margin + indent;
          if (/^\s+$/.test(w)) return; // don't start a wrapped line with space
        }
        doc.text(w, x, state.y);
        x += wWidth;
      });
    });
    state.y += lineHeight;
  };

  // Render markdown-ish text: ## headings, **bold**, - bullets, paragraphs.
  const writeMarkdown = (raw, baseFontSize = 10) => {
    const text = stripVizTags(raw).trim();
    if (!text) return;
    const blocks = text.split('\n');
    blocks.forEach((rawLine) => {
      const lineStr = rawLine.replace(/\t/g, '  ');
      const line = lineStr.trim();
      if (!line) { state.y += baseFontSize * 0.6; return; }

      const heading = line.match(/^(#{1,6})\s+(.*)$/);
      if (heading) {
        const level = heading[1].length;
        const size = level <= 2 ? baseFontSize + 4 : baseFontSize + 2;
        state.y += 4;
        writeRichLine(heading[2], size, 'bold');
        return;
      }
      const bullet = line.match(/^[-*]\s+(.*)$/);
      if (bullet) {
        ensureSpace(baseFontSize * 1.4);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(baseFontSize);
        doc.text('•', margin + 6, state.y);
        writeRichLine(bullet[1], baseFontSize, 'normal', 18);
        return;
      }
      writeRichLine(line, baseFontSize, 'normal');
    });
  };

  // Draw a simple bordered table from headers + rows (arrays of strings).
  const drawTable = (headers, rows, fontSize = 9) => {
    if (!headers.length) return;
    const colCount = headers.length;
    const colWidth = maxWidth / colCount;
    const cellPad = 4;
    const lineH = fontSize * 1.3;

    const renderRow = (cells, isHeader) => {
      // Pre-wrap each cell to know row height.
      doc.setFont('helvetica', isHeader ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      const wrapped = cells.map((c) =>
        doc.splitTextToSize(String(c == null ? '' : c), colWidth - cellPad * 2),
      );
      const rowLines = Math.max(...wrapped.map((w) => w.length), 1);
      const rowHeight = rowLines * lineH + cellPad;
      ensureSpace(rowHeight);
      if (isHeader) {
        doc.setFillColor(238, 240, 247);
        doc.rect(margin, state.y - lineH + 2, maxWidth, rowHeight, 'F');
      }
      // Cell text + vertical borders.
      wrapped.forEach((lines, ci) => {
        const cx = margin + ci * colWidth + cellPad;
        lines.forEach((ln, li) => {
          doc.text(ln, cx, state.y + li * lineH);
        });
      });
      // Borders.
      doc.setDrawColor(210);
      doc.rect(margin, state.y - lineH + 2, maxWidth, rowHeight);
      for (let ci = 1; ci < colCount; ci++) {
        doc.line(margin + ci * colWidth, state.y - lineH + 2, margin + ci * colWidth, state.y - lineH + 2 + rowHeight);
      }
      state.y += rowHeight;
    };

    renderRow(headers, true);
    rows.forEach((r) => renderRow(r, false));
    state.y += 6;
  };

  // Render a visualization object (table or chart) as a PDF table.
  const drawVisualization = (viz) => {
    if (!viz || !Array.isArray(viz.data) || viz.data.length === 0) return;
    if (viz.title) {
      state.y += 4;
      writeRichLine(viz.title, 11, 'bold');
    }
    const type = viz.chartType || viz.type;
    if (type === 'table' && Array.isArray(viz.columns) && viz.columns.length) {
      // Columns are plain strings (the data keys themselves), matching the live
      // table renderer (dataIndex: col). Tolerate object columns too.
      const keys = viz.columns.map((c) =>
        typeof c === 'string' ? c : (c.key || c.dataKey || c.dataIndex || c.label || c.title),
      );
      const headers = viz.columns.map((c) => {
        if (typeof c === 'string') {
          return c.charAt(0).toUpperCase() + c.slice(1).replace(/([A-Z])/g, ' $1');
        }
        return c.label || c.title || c.key || String(c);
      });
      const rows = viz.data.map((row) => keys.map((k) => row[k]));
      drawTable(headers, rows);
    } else {
      // Chart types → render their underlying data as a 2-column table.
      const first = viz.data[0] || {};
      const keys = Object.keys(first);
      const nameKey = viz.nameKey || keys[0];
      const valueKey = viz.valueKey || keys[1] || keys[0];
      const headers = [nameKey, valueKey];
      const rows = viz.data.map((row) => [row[nameKey], row[valueKey]]);
      drawTable(headers, rows);
    }
  };

  return { state, ensureSpace, writeMarkdown, drawTable, drawVisualization, writeRichLine, pageWidth, maxWidth };
}

/**
 * Build and download a PDF report.
 * @param {Object} report
 * @param {string} report.department
 * @param {string} report.startDate
 * @param {string} report.endDate
 * @param {string} report.overview        AI department roll-up
 * @param {Array}  report.members          [{ name, email, position, summary, status }]
 * @param {Object} report.stats            { memberCount, withData }
 */
export function downloadDepartmentReportPDF(report) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const c = makePdfCursor(doc, margin);
  const dateLabel = report.startDate === report.endDate
    ? report.startDate
    : `${report.startDate} → ${report.endDate}`;

  // --- Cover band (purple gradient-ish header) ---
  doc.setFillColor(99, 102, 241);
  doc.rect(0, 0, c.pageWidth, 110, 'F');
  doc.setTextColor(255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('DEPARTMENT DAILY REPORT', margin, 36);
  doc.setFontSize(24);
  doc.text(String(report.department), margin, 64);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(dateLabel, margin, 86);
  doc.setTextColor(0);
  c.state.y = 132;

  // --- Headline ---
  if (report.headline) {
    c.writeMarkdown(report.headline, 12);
    c.state.y += 6;
  }

  // --- Stat line ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(80);
  const statParts = [
    `Members: ${report.stats.memberCount}`,
    `Active: ${report.stats.withData}`,
  ];
  if (report.stats.totalHours != null) statParts.push(`Total Hours: ${report.stats.totalHours}h`);
  statParts.push(`Generated: ${new Date().toLocaleString()}`);
  c.ensureSpace(16);
  doc.text(statParts.join('     |     '), margin, c.state.y);
  c.state.y += 14;
  doc.setTextColor(0);
  doc.setDrawColor(220);
  doc.line(margin, c.state.y, c.pageWidth - margin, c.state.y);
  c.state.y += 18;

  const hasStructured = Array.isArray(report.sections) && report.sections.length > 0;

  if (hasStructured) {
    report.sections.forEach((s) => {
      c.ensureSpace(40);
      doc.setTextColor(99, 102, 241);
      c.writeRichLine(s.title || 'Section', 13, 'bold');
      doc.setTextColor(0);
      c.state.y += 2;
      if (s.body) c.writeMarkdown(s.body, 10.5);
      if (Array.isArray(s.bullets)) {
        s.bullets.forEach((b) => c.writeMarkdown(`- ${b}`, 10.5));
      }
      c.state.y += 10;
    });

    // Per-member highlights as a table.
    if (Array.isArray(report.memberHighlights) && report.memberHighlights.length > 0) {
      c.ensureSpace(40);
      doc.setTextColor(99, 102, 241);
      c.writeRichLine('Per-Member Highlights', 13, 'bold');
      doc.setTextColor(0);
      c.state.y += 6;
      const headers = ['Member', 'Hours', 'Highlight'];
      const rows = report.memberHighlights.map((m) => [
        m.name,
        m.hours != null ? `${Number(m.hours).toFixed(2)}h` : '',
        m.highlight || '',
      ]);
      c.drawTable(headers, rows, 9);
    }
  } else {
    // Fallback: raw text.
    c.writeMarkdown(report.overview || 'No report generated.', 11);
  }

  const safeDept = String(report.department).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  doc.save(`department-report-${safeDept}-${report.startDate}_to_${report.endDate}.pdf`);
}

/**
 * Build and download a single-member work-summary PDF.
 * Reused by the normal Daily Summary view's "Download PDF" button.
 * @param {Object} m { name, position, startDate, endDate, summary, visualizations }
 */
export function downloadSingleUserSummaryPDF(m) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const c = makePdfCursor(doc, margin);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Work Summary', margin, c.state.y); c.state.y += 28;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text(`${m.name}${m.position ? ` — ${m.position}` : ''}`, margin, c.state.y); c.state.y += 18;
  doc.setFontSize(11);
  doc.setTextColor(90);
  const dateLabel = m.startDate === m.endDate ? m.startDate : `${m.startDate} to ${m.endDate}`;
  doc.text(`Date: ${dateLabel}`, margin, c.state.y); c.state.y += 14;
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, c.state.y); c.state.y += 22;
  doc.setTextColor(0);

  doc.setDrawColor(220);
  doc.line(margin, c.state.y, c.pageWidth - margin, c.state.y); c.state.y += 18;

  c.writeMarkdown(m.summary || '—', 11);
  (m.visualizations || []).forEach((viz) => c.drawVisualization(viz));

  const safeName = String(m.name).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  doc.save(`work-summary-${safeName}-${dateLabel.replace(/\s+/g, '')}.pdf`);
}

/**
 * Build and download an Excel workbook (one row per member + an overview sheet).
 */
export function downloadDepartmentReportExcel(report) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: overview / meta + sections
  const overviewRows = [
    ['Department Daily Report'],
    [],
    ['Department', report.department],
    ['Date range', `${report.startDate} to ${report.endDate}`],
    ['Members', report.stats.memberCount],
    ['Active', report.stats.withData],
    ['Total hours', report.stats.totalHours != null ? report.stats.totalHours : ''],
    ['Generated', new Date().toLocaleString()],
    [],
  ];
  if (report.headline) overviewRows.push(['Summary', cleanText(report.headline)], []);
  if (Array.isArray(report.sections)) {
    report.sections.forEach((s) => {
      overviewRows.push([s.title || 'Section']);
      if (s.body) overviewRows.push([cleanText(s.body)]);
      if (Array.isArray(s.bullets)) s.bullets.forEach((b) => overviewRows.push(['• ' + cleanText(b)]));
      overviewRows.push([]);
    });
  } else if (report.overview) {
    overviewRows.push(['Report'], [cleanText(report.overview)]);
  }
  const ws1 = XLSX.utils.aoa_to_sheet(overviewRows);
  ws1['!cols'] = [{ wch: 24 }, { wch: 110 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Report');

  // Sheet 2: per-member highlights
  const source = Array.isArray(report.memberHighlights) ? report.memberHighlights : [];
  const memberRows = source.map((m) => ({
    Member: m.name,
    Hours: m.hours != null ? Number(m.hours).toFixed(2) : '',
    Highlight: cleanText(m.highlight || ''),
  }));
  const ws2 = XLSX.utils.json_to_sheet(memberRows.length ? memberRows : [{ Member: '', Hours: '', Highlight: '' }]);
  ws2['!cols'] = [{ wch: 26 }, { wch: 10 }, { wch: 110 }];
  XLSX.utils.book_append_sheet(wb, ws2, 'Members');

  const safeDept = String(report.department).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  XLSX.writeFile(wb, `department-report-${safeDept}-${report.startDate}_to_${report.endDate}.xlsx`);
}
